/**
 * apiAuth.js - DB-backed authentication context.
 *
 * Replaces the old MSAL-based useAuth() hook. Sign-in goes through
 * the Express backend at /api/auth/login, which validates credentials
 * against vms_db.users and returns a bearer token. The token is stored
 * in localStorage so it survives page reloads.
 *
 * Public API (drop-in for the MSAL version):
 *   const { user, token, isAuthenticated, login, logout, refresh, getAccessToken } = useAuth();
 *
 *   login({ username, password })
 *   logout()
 *   refresh()              - re-fetch the user profile from /api/auth/me
 *   getAccessToken()       - returns the bearer token used for BC proxy
 */

import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { API_BASE } from "../config";

const STORAGE_KEY = "vms.session";

function readSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.token) return null;
    if (parsed.expires_at && new Date(parsed.expires_at).getTime() < Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeSession(s) {
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
}

/* ------------------------------------------------------------------ */
/* Bridge for non-React callers (axios interceptors, etc.)              */
/* ------------------------------------------------------------------ */
/* The AuthProvider keeps a module-scoped copy of the current token
 * so callers that live outside React (like the BC axios interceptor
 * in api/bcClient.js) can read it without holding a hook reference. */

let _moduleToken = null;

export function getAccessToken() {
  // Prefer the live in-memory token; fall back to localStorage so the
  // very first render before the AuthProvider mounts still has a value.
  if (_moduleToken) return _moduleToken;
  const s = readSession();
  return s?.token || null;
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(() => {
    const s = readSession();
    _moduleToken = s?.token || null;
    return s;
  });
  const [loading, setLoading] = useState(false);

  // Keep the module-scoped token in sync with state so axios clients
  // outside the React tree pick up logins and logouts immediately.
  useEffect(() => {
    _moduleToken = session?.token || null;
  }, [session]);

  // Re-validate on mount: ask /api/auth/me to confirm the token still works.
  useEffect(() => {
    if (!session?.token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${session.token}` },
        });
        if (!r.ok) {
          if (!cancelled) {
            writeSession(null);
            setSession(null);
          }
          return;
        }
        const json = await r.json();
        if (!cancelled && json?.user) {
          setSession((s) => (s ? { ...s, user: json.user } : s));
        }
      } catch {
        // network down - keep the cached session, will retry next mount
      }
    })();
    return () => {
      cancelled = true;
    };
    // run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async ({ username, password }) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        throw new Error(data.error || `Login failed (HTTP ${r.status})`);
      }
      writeSession(data);
      setSession(data);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    const tok = session?.token;
    try {
      if (tok) {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${tok}` },
        });
      }
    } catch {
      // ignore - we always clear locally
    }
    writeSession(null);
    setSession(null);
  }, [session?.token]);

  const refresh = useCallback(async () => {
    if (!session?.token) return null;
    const r = await fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${session.token}` },
    });
    if (!r.ok) return null;
    const json = await r.json();
    if (json?.user) {
      setSession((s) => (s ? { ...s, user: json.user } : s));
    }
    return json.user;
  }, [session?.token]);

  const getAccessToken = useCallback(() => session?.token || null, [session?.token]);

  const value = useMemo(
    () => ({
      user: session?.user || null,
      token: session?.token || null,
      expiresAt: session?.expires_at || null,
      isAuthenticated: Boolean(session?.token && session?.user),
      loading,
      login,
      logout,
      refresh,
      getAccessToken,
    }),
    [session, loading, login, logout, refresh, getAccessToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    // Sensible default so component code can mount even if the provider
    // is missing (e.g. during early bootstrapping). Render still
    // surfaces a console warning.
    if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.warn("useAuth() used outside <AuthProvider>; using no-op defaults.");
    }
    return {
      user: null,
      token: null,
      expiresAt: null,
      isAuthenticated: false,
      loading: false,
      login: async () => {
        throw new Error("AuthProvider not mounted");
      },
      logout: () => {},
      refresh: async () => null,
      getAccessToken: () => null,
    };
  }
  return ctx;
}